import React, { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { pmService, type ClaimStatusRow, type ClaimComment } from "../../services/pm.service";
import { SearchableMultiSelect } from "../ui/SearchableMultiSelect";
import { Select } from "../ui/Select";
import { DatePicker } from "../ui/DatePicker";
import { computePeriodRange, type PmPeriodType } from "../../utils/period";
import { ProfileService } from "../../services/profile.service";
import { ROLES } from "../../utils/roles";

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 2000;

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};
const STATUS_OPTIONS = ["Approved", "Denied", "Paid", "Unpaid"];

const SEARCH_BY_OPTIONS: { key: string; label: string }[] = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "patientId", label: "Patient ID" },
  { key: "encounterId", label: "Encounter #" },
  { key: "ehrClaimId", label: "EHR Claim #" },
  { key: "cptCode", label: "CPT Code" },
  { key: "insurer", label: "Insurer" },
];

const DEFAULT_SEARCH_BY = ["firstName", "lastName", "cptCode"];

interface SearchBySelectorProps {
  selected: string[];
  onChange: (keys: string[]) => void;
}

const SearchBySelector: React.FC<SearchBySelectorProps> = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (key: string) => {
    if (selected.includes(key)) {
      if (selected.length === 1) return;
      onChange(selected.filter((k) => k !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  const label =
    selected.length <= 2
      ? selected.map((k) => SEARCH_BY_OPTIONS.find((o) => o.key === k)?.label ?? k).join(", ")
      : `${selected.length} fields`;

  return (
    <div ref={ref} className="relative flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-9 px-3 text-xs border-2 border-r-0 border-border-gray bg-white hover:bg-gray-50 whitespace-nowrap flex items-center gap-1.5 text-gray-700"
      >
        <span>Search by: {label}</span>
        <svg
          className="w-3 h-3 text-gray-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 bg-white border-2 border-border-gray shadow-lg min-w-[160px]">
          {SEARCH_BY_OPTIONS.map(({ key, label: optLabel }) => (
            <label
              key={key}
              className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-gray-50 select-none"
            >
              <input
                type="checkbox"
                checked={selected.includes(key)}
                onChange={() => toggle(key)}
                className="w-3.5 h-3.5 accent-primary-blue"
              />
              {optLabel}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

type Priority = "Primary" | "Secondary";
type SortCol =
  | "lastName"
  | "firstName"
  | "patientId"
  | "encounter"
  | "ehrClaimId"
  | "insurer"
  | "cptCode"
  | "status"
  | "dateOfService"
  | "lastVisit"
  | "totalCharge"
  | "outstanding";
type SortDir = "asc" | "desc";

interface FilterState {
  periodType: PmPeriodType;
  customFrom: string;
  customTo: string;
  insurers: string[];
  cptCodes: string[];
  statuses: string[];
  priority: Priority;
}

const defaultCustom = computePeriodRange("current_month");

const defaultFilter = (): FilterState => ({
  periodType: "current_month",
  customFrom: defaultCustom.dateFrom,
  customTo: defaultCustom.dateTo,
  insurers: [],
  cptCodes: [],
  statuses: [],
  priority: "Primary",
});

const arrsEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

const filtersEqual = (a: FilterState, b: FilterState) =>
  a.periodType === b.periodType &&
  a.customFrom === b.customFrom &&
  a.customTo === b.customTo &&
  arrsEqual(a.insurers, b.insurers) &&
  arrsEqual(a.cptCodes, b.cptCodes) &&
  arrsEqual(a.statuses, b.statuses) &&
  a.priority === b.priority;

const STATUS_BADGE: Record<string, string> = {
  Denied: "bg-red-100 text-red-700",
  Paid: "bg-green-100 text-green-700",
  Approved: "bg-blue-100 text-blue-700",
  Unpaid: "bg-amber-100 text-amber-700",
};

interface SortHeaderProps {
  label: string;
  col: SortCol;
  sortBy: SortCol | null;
  sortDir: SortDir;
  onSort: (col: SortCol) => void;
  className?: string;
}

const SortHeader: React.FC<SortHeaderProps> = ({
  label,
  col,
  sortBy,
  sortDir,
  onSort,
  className = "",
}) => (
  <th
    onClick={() => onSort(col)}
    className={`px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 ${className}`}
  >
    <span className="flex items-center gap-1">
      {label}
      {sortBy === col ? (
        <span className="text-primary-blue">{sortDir === "asc" ? "↑" : "↓"}</span>
      ) : (
        <span className="text-gray-300">↕</span>
      )}
    </span>
  </th>
);

const formatCommentDate = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

interface CommentPanelProps {
  row: ClaimStatusRow;
  extraComments: ClaimComment[];
  canPost: boolean;
  currentUserEmail: string;
  onPost: (body: string) => Promise<void>;
  onDelete: (commentId: string) => Promise<void>;
  onClose: () => void;
}

const CommentPanel: React.FC<CommentPanelProps> = ({
  row,
  extraComments,
  canPost,
  currentUserEmail,
  onPost,
  onDelete,
  onClose,
}) => {
  const [input, setInput] = useState("");
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const allComments = [...(row.comments ?? []), ...extraComments];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || posting) return;
    setPosting(true);
    setError(null);
    try {
      await onPost(trimmed);
      setInput("");
    } catch {
      setError("Failed to post comment.");
    } finally {
      setPosting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const title = [row.firstName, row.lastName].filter(Boolean).join(" ") || row.patientId;

  return (
    <div className="fixed inset-0 z-50 flex justify-end pointer-events-none">
      <div
        ref={panelRef}
        className="pointer-events-auto w-full max-w-sm bg-white border-l-2 border-border-gray shadow-xl flex flex-col h-full"
      >
        {/* Panel header */}
        <div className="bg-primary-blue text-white px-4 py-3 flex items-start justify-between flex-shrink-0">
          <div>
            <div className="font-bold text-sm">{title}</div>
            <div className="text-xs opacity-75 mt-0.5">
              CPT {row.cptCode || "—"} · Claim {row.ehrClaimId || "—"}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-3 mt-0.5 text-white opacity-75 hover:opacity-100"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Comments thread */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {allComments.length === 0 && (
            <p className="text-xs text-gray-400 text-center mt-6">No comments yet.</p>
          )}
          {allComments.map((c) => (
            <div key={c.id} className="bg-gray-50 border border-border-gray p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-gray-700 truncate">{c.authorEmail}</span>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  <span className="text-xs text-gray-400">{formatCommentDate(c.createdAt)}</span>
                  {c.authorEmail === currentUserEmail && (
                    <button
                      type="button"
                      disabled={deletingId === c.id}
                      onClick={async () => {
                        setDeletingId(c.id);
                        try {
                          await onDelete(c.id);
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                      className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Delete comment"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-800 whitespace-pre-wrap break-words">{c.body}</p>
            </div>
          ))}
        </div>

        {/* Input (admin only) */}
        {canPost && (
          <div className="border-t-2 border-border-gray px-4 py-3 flex-shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a comment… (Ctrl+Enter to send)"
              rows={3}
              maxLength={4000}
              className="w-full text-xs border-2 border-border-gray p-2 resize-none focus:outline-none focus:border-primary-blue"
            />
            {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">{input.length}/4000</span>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={posting || !input.trim()}
                className="px-4 py-1.5 bg-primary-blue text-white text-xs hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {posting ? "Posting…" : "Post"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const PatientMonitoringWidget: React.FC = () => {
  const [pending, setPending] = useState<FilterState>(defaultFilter);
  const [applied, setApplied] = useState<FilterState>(defaultFilter);
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchBy, setSearchBy] = useState<string[]>(DEFAULT_SEARCH_BY);

  const [sortBy, setSortBy] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const [reviewedOverrides, setReviewedOverrides] = useState<Record<string, boolean>>({});
  const [isBulkReviewing, setIsBulkReviewing] = useState(false);

  const [commentPanel, setCommentPanel] = useState<ClaimStatusRow | null>(null);
  const [pendingComments, setPendingComments] = useState<Record<string, ClaimComment[]>>({});

  const profile = ProfileService.getUserProfileFromStorage();
  const isReportingAdmin =
    profile?.roles?.includes(ROLES.CUSTOM_REPORTING_ADMIN) ||
    profile?.roles?.includes(ROLES.ADMIN) ||
    false;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // reset page on sort change
  useEffect(() => {
    setPage(1);
  }, [sortBy, sortDir]);

  const hasChanges = !filtersEqual(pending, applied);

  const { dateFrom, dateTo } = computePeriodRange(
    applied.periodType,
    applied.customFrom,
    applied.customTo
  );

  const { data: insurersData } = useQuery({
    queryKey: ["pm-insurers"],
    queryFn: () => pmService.getInsurers(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: cptData } = useQuery({
    queryKey: ["pm-cpt-codes"],
    queryFn: () => pmService.getCptCodes(),
    staleTime: 5 * 60 * 1000,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [
      "pm-claims",
      applied.insurers,
      applied.cptCodes,
      applied.statuses,
      applied.priority,
      dateFrom,
      dateTo,
      appliedSearch,
      searchBy,
      sortBy,
      sortDir,
      page,
    ],
    queryFn: () =>
      pmService.searchClaims({
        insurers: applied.insurers.length > 0 ? applied.insurers : undefined,
        cptCodes: applied.cptCodes.length > 0 ? applied.cptCodes : undefined,
        statuses: applied.statuses.length > 0 ? applied.statuses : undefined,
        priority: applied.priority,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: appliedSearch || undefined,
        searchBy: appliedSearch ? searchBy : undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: sortBy ? sortDir : undefined,
        page,
        limit: PAGE_SIZE,
      }),
    staleTime: 0,
    gcTime: 0, // remove cache to see comments
  });

  const rows: ClaimStatusRow[] = result?.rows ?? [];
  const total = result?.total ?? 0;
  const uniqueClaims = result?.uniqueClaims ?? 0;
  const totalPages = result?.totalPages ?? 0;

  const pageServiceLineIds = rows.map((r) => r.serviceLineId).filter(Boolean);
  const allPageReviewed =
    pageServiceLineIds.length > 0 &&
    pageServiceLineIds.every((id) =>
      id in reviewedOverrides
        ? reviewedOverrides[id]
        : (rows.find((r) => r.serviceLineId === id)?.reviewed ?? false)
    );
  const somePageReviewed = pageServiceLineIds.some((id) =>
    id in reviewedOverrides
      ? reviewedOverrides[id]
      : (rows.find((r) => r.serviceLineId === id)?.reviewed ?? false)
  );

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = somePageReviewed && !allPageReviewed;
    }
  }, [somePageReviewed, allPageReviewed]);

  const handleBulkToggle = useCallback(async () => {
    if (pageServiceLineIds.length === 0 || isBulkReviewing) return;
    const newVal = !allPageReviewed;
    const snapshot: Record<string, boolean> = {};
    pageServiceLineIds.forEach((id) => {
      snapshot[id] =
        id in reviewedOverrides
          ? reviewedOverrides[id]
          : (rows.find((r) => r.serviceLineId === id)?.reviewed ?? false);
    });
    setReviewedOverrides((prev) => {
      const next = { ...prev };
      pageServiceLineIds.forEach((id) => {
        next[id] = newVal;
      });
      return next;
    });
    setIsBulkReviewing(true);
    try {
      await pmService.bulkReview(pageServiceLineIds, newVal);
    } catch {
      setReviewedOverrides((prev) => {
        const next = { ...prev };
        pageServiceLineIds.forEach((id) => {
          next[id] = snapshot[id];
        });
        return next;
      });
    } finally {
      setIsBulkReviewing(false);
    }
  }, [pageServiceLineIds, allPageReviewed, isBulkReviewing, reviewedOverrides, rows]);

  const handleApply = () => {
    setApplied(pending);
    setPage(1);
    setExportError(null);
    setReviewedOverrides({});
    setPendingComments({});
    setCommentPanel(null);
  };

  const handlePostComment = async (body: string) => {
    if (!commentPanel) return;
    const newComment = await pmService.addComment("service_line", commentPanel.serviceLineId, body);
    setPendingComments((prev) => ({
      ...prev,
      [commentPanel.serviceLineId]: [...(prev[commentPanel.serviceLineId] ?? []), newComment],
    }));
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!commentPanel) return;
    await pmService.deleteComment("service_line", commentPanel.serviceLineId, commentId);
    setPendingComments((prev) => ({
      ...prev,
      [commentPanel.serviceLineId]: (prev[commentPanel.serviceLineId] ?? []).filter(
        (c) => c.id !== commentId
      ),
    }));
    setCommentPanel((prev) =>
      prev ? { ...prev, comments: (prev.comments ?? []).filter((c) => c.id !== commentId) } : prev
    );
  };

  const isReviewed = (row: { serviceLineId: string; reviewed: boolean }) =>
    row.serviceLineId in reviewedOverrides ? reviewedOverrides[row.serviceLineId] : row.reviewed;

  const toggleReview = async (row: { serviceLineId: string; reviewed: boolean }) => {
    const newVal = !isReviewed(row);
    setReviewedOverrides((prev) => ({ ...prev, [row.serviceLineId]: newVal }));
    try {
      await pmService.setReview(row.serviceLineId, newVal);
    } catch {
      setReviewedOverrides((prev) => ({ ...prev, [row.serviceLineId]: !newVal }));
    }
  };

  const handleSort = (col: SortCol) => {
    if (sortBy === col) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else {
        setSortBy(null);
        setSortDir("desc");
      }
    } else {
      setSortBy(col);
      setSortDir("asc");
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setAppliedSearch(searchInput.trim());
      setPage(1);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await pmService.downloadClaimsXLSX({
        insurers: applied.insurers.length > 0 ? applied.insurers : undefined,
        cptCodes: applied.cptCodes.length > 0 ? applied.cptCodes : undefined,
        statuses: applied.statuses.length > 0 ? applied.statuses : undefined,
        priority: applied.priority,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        search: appliedSearch || undefined,
        searchBy: appliedSearch ? searchBy : undefined,
        sortBy: sortBy ?? undefined,
        sortOrder: sortBy ? sortDir : undefined,
      });
    } catch (e) {
      if (e instanceof Error && e.message === "too_large") {
        setExportError("Too many rows — narrow your filters and try again.");
      } else {
        setExportError("Export failed. Please try again.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {commentPanel && (
        <CommentPanel
          row={commentPanel}
          extraComments={pendingComments[commentPanel.serviceLineId] ?? []}
          canPost={isReportingAdmin}
          currentUserEmail={profile?.email ?? ""}
          onPost={handlePostComment}
          onDelete={handleDeleteComment}
          onClose={() => setCommentPanel(null)}
        />
      )}
      <div className="bg-white border-2 border-border-gray">
        {/* Header */}
        <div className="bg-primary-blue text-white px-4 md:px-6 py-3">
          <h3 className="font-bold text-lg">Patient Monitoring</h3>
          <span className="text-xs sm:text-sm">
            Claim status by patient for the selected period
          </span>
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 border-b-2 border-border-gray flex flex-wrap gap-3 items-end">
          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">Period</label>
            <Select
              value={pending.periodType}
              onChange={(e) =>
                setPending((prev) => ({ ...prev, periodType: e.target.value as PmPeriodType }))
              }
            >
              <option value="current_week">Current Week</option>
              <option value="previous_week">Previous Week</option>
              <option value="current_month">Current Month</option>
              <option value="previous_month">Previous Month</option>
              <option value="last_90_days">Last 90 Days</option>
              <option value="year_to_date">Year to Date</option>
              <option value="last_year">Last Year</option>
              <option value="custom">Custom Period</option>
            </Select>
          </div>

          {pending.periodType === "custom" && (
            <>
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
                <DatePicker
                  value={pending.customFrom}
                  maxDate={pending.customTo || undefined}
                  onChange={(v) => setPending((prev) => ({ ...prev, customFrom: v }))}
                />
              </div>
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-gray-700 mb-1">End date</label>
                <DatePicker
                  value={pending.customTo}
                  minDate={pending.customFrom || undefined}
                  onChange={(v) => setPending((prev) => ({ ...prev, customTo: v }))}
                />
              </div>
            </>
          )}

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">Insurer</label>
            <SearchableMultiSelect
              options={insurersData?.insurers ?? []}
              selectedOptions={pending.insurers}
              onSelectionChange={(v) => setPending((prev) => ({ ...prev, insurers: v }))}
              label="Insurers"
              emptyLabel="All Insurers"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">CPT Code</label>
            <SearchableMultiSelect
              options={cptData?.cptCodes ?? []}
              selectedOptions={pending.cptCodes}
              onSelectionChange={(v) => setPending((prev) => ({ ...prev, cptCodes: v }))}
              label="CPT Codes"
              emptyLabel="All CPT Codes"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <SearchableMultiSelect
              options={STATUS_OPTIONS}
              selectedOptions={pending.statuses}
              onSelectionChange={(v) => setPending((prev) => ({ ...prev, statuses: v }))}
              label="Statuses"
              emptyLabel="All Statuses"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-medium text-gray-700 mb-1">Payer</label>
            <div className="flex border-2 border-border-gray h-10">
              {(["Primary", "Secondary"] as Priority[]).map((p, i) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPending((prev) => ({ ...prev, priority: p }))}
                  className={`px-4 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                    i > 0 ? "border-l-2 border-border-gray" : ""
                  } ${
                    pending.priority === p
                      ? "bg-primary-blue text-white"
                      : "bg-white text-black hover:bg-gray-50"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={handleApply}
            disabled={!hasChanges}
            className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
          >
            Apply
          </button>

          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting || isLoading || total === 0}
            className="h-10 px-6 bg-primary-blue text-white text-xs sm:text-sm hover:bg-indigo-darker-blue disabled:bg-gray-300 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 transition-colors"
          >
            {isExporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-gray-50 border-b-2 border-border-gray">
          <div className="flex items-center w-full min-w-[200px]">
            <SearchBySelector selected={searchBy} onChange={setSearchBy} />
            <div className="relative flex-1">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search…"
                className="w-full h-9 pl-9 pr-8 text-sm border-2 border-border-gray bg-white focus:outline-none focus:border-primary-blue"
              />
              <svg
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setAppliedSearch("");
                    setPage(1);
                  }}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 flex items-center justify-center"
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-4 h-4">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {exportError && (
          <div className="px-4 py-2 bg-red-50 border-b-2 border-red-200 text-xs text-red-700">
            {exportError}
          </div>
        )}

        {/* Table */}
        <div>
          {isLoading && <div className="text-center py-8 text-gray-500">Loading claims...</div>}

          {!isLoading && total === 0 && (
            <div className="text-center py-8 text-gray-500">
              No claims found for the selected filters.
            </div>
          )}

          {!isLoading && total > 0 && (
            <>
              <div className="bg-gray-50 border-b-2 border-border-gray px-4 md:px-6 py-2 text-xs font-bold text-gray-700 uppercase tracking-wide flex gap-4">
                <span>{uniqueClaims.toLocaleString()} claims found</span>
                <span className="font-normal text-gray-500">
                  ({total.toLocaleString()} service lines)
                </span>
              </div>

              <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "480px" }}>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b-2 border-border-gray sticky top-0 z-10">
                    <tr>
                      <SortHeader
                        label="Last Name"
                        col="lastName"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="First Name"
                        col="firstName"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Patient ID"
                        col="patientId"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Encounter #"
                        col="encounter"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="EHR Claim #"
                        col="ehrClaimId"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Date of Service"
                        col="dateOfService"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Last Visit"
                        col="lastVisit"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Insurer"
                        col="insurer"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="CPT Code"
                        col="cptCode"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Status"
                        col="status"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Total Charge"
                        col="totalCharge"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <SortHeader
                        label="Outstanding"
                        col="outstanding"
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <th className="px-3 py-3 text-left text-xs font-bold text-gray-700 uppercase whitespace-nowrap">
                        Comments
                      </th>
                      <th className="px-3 py-3 w-8 bg-gray-50">
                        <input
                          ref={headerCheckboxRef}
                          type="checkbox"
                          checked={allPageReviewed}
                          onChange={handleBulkToggle}
                          disabled={isBulkReviewing || pageServiceLineIds.length === 0}
                          className="w-4 h-4 accent-primary-blue cursor-pointer disabled:cursor-not-allowed"
                          title="Mark / unmark all rows on this page as reviewed"
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-gray">
                    {rows.map((row) => (
                      <tr
                        key={`${row.serviceLineId || row.ehrClaimId}-${row.cptCode}`}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-3 py-2 whitespace-nowrap font-medium">
                          {row.lastName || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">{row.firstName || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.patientId}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.encounterId || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.ehrClaimId || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {formatDate(row.dateOfService)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {formatDate(row.lastVisit)}
                        </td>
                        <td className="px-3 py-2 text-gray-600">{row.insurer || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.cptCode || "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span
                            className={`inline-block text-xs px-2 py-0.5 rounded font-medium ${
                              STATUS_BADGE[row.status] ?? "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.totalCharge > 0
                            ? `$${row.totalCharge.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                          {row.outstanding > 0
                            ? `$${row.outstanding.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : "—"}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {(() => {
                            const count =
                              (row.comments?.length ?? 0) +
                              (pendingComments[row.serviceLineId]?.length ?? 0);
                            const isOpen = commentPanel?.serviceLineId === row.serviceLineId;
                            return (
                              <button
                                type="button"
                                onClick={() => setCommentPanel(isOpen ? null : row)}
                                className={`flex items-center gap-1 text-xs px-2 py-1 border transition-colors ${
                                  isOpen
                                    ? "border-primary-blue bg-blue-50 text-primary-blue"
                                    : count > 0
                                      ? "border-primary-blue text-primary-blue hover:bg-blue-50"
                                      : "border-border-gray text-gray-400 hover:border-gray-400 hover:text-gray-600"
                                }`}
                                title={
                                  count > 0
                                    ? `${count} comment${count !== 1 ? "s" : ""}`
                                    : "Add comment"
                                }
                              >
                                <svg
                                  className="w-3.5 h-3.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                  />
                                </svg>
                                {count > 0 && <span className="font-medium">{count}</span>}
                              </button>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 w-8">
                          <input
                            type="checkbox"
                            checked={isReviewed(row)}
                            onChange={() => toggleReview(row)}
                            className="w-4 h-4 accent-primary-blue cursor-pointer"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between py-3 px-4 border-t-2 border-border-gray">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 text-xs border-2 border-border-gray bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-xs text-gray-600">
                    {(page - 1) * PAGE_SIZE + 1}–
                    {Math.min(page * PAGE_SIZE, total).toLocaleString()} of {total.toLocaleString()}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 text-xs border-2 border-border-gray bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
